import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { User, Building2, ArrowLeft, CheckCircle } from "lucide-react";
import { agentAdvancerSenzaSfondo } from '../images';

const SceltaTipoUtente = () => {
  const navigate = useNavigate();

  const userTypes = [
    {
      type: "candidato",
      title: "Sono un Candidato",
      subtitle: "Trova l'agenzia perfetta per la tua carriera nel settore immobiliare.",
      icon: User,
      color: "blue",
      benefits: [
        "Match intelligente basato sulla tua zona",
        "Colloqui diretti con le agenzie",
        "Profilo professionale completo",
        "Accesso gratuito e senza limiti"
      ],
      route: "/candidato-onboarding"
    },
    {
      type: "agenzia", 
      title: "Sono un'Agenzia Immobiliare",
      subtitle: "Scopri nuovi agenti qualificati per far crescere il tuo team.",
      icon: Building2,
      color: "green",
      benefits: [
        "Sistema di matching avanzato",
        "Gestione colloqui integrata",
        "Dashboard intuitiva e completa",
        "Possibilità di test gratuito"
      ],
      route: "/agenzia-onboarding"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        gradient: "from-blue-500 to-blue-600",
        bg: "bg-blue-50",
        text: "text-blue-600",
        button: "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700",
        border: "border-blue-200"
      },
      green: {
        gradient: "from-green-500 to-green-600", 
        bg: "bg-green-50",
        text: "text-green-600",
        button: "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700",
        border: "border-green-200"
      }
    };
    return colors[color];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/accesso')}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1">
              <img 
                src={agentAdvancerSenzaSfondo} 
                alt="InLeads Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-semibold text-gray-900">InLeads</span>
          </div>
        </div>

        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Come vuoi iniziare?
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Scegli il tuo percorso per accedere alla piattaforma di recruiting immobiliare più avanzata
          </p>
        </div>

        {/* User Type Cards */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {userTypes.map((userType) => {
              const colors = getColorClasses(userType.color);
              const Icon = userType.icon;

              return (
                <Card 
                  key={userType.type}
                  className={`border-0 shadow-2xl rounded-3xl overflow-hidden hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 ${colors.border} border-2`}
                >
                  <CardHeader className={`${colors.bg} pb-6`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 bg-gradient-to-br ${colors.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl font-bold text-gray-900">
                          {userType.title}
                        </CardTitle>
                        <p className={`${colors.text} font-medium mt-1`}>
                          {userType.subtitle}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-6">
                    {/* Benefits List */}
                    <div className="space-y-3 mb-8">
                      {userType.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <CheckCircle className={`w-5 h-5 ${colors.text} flex-shrink-0`} />
                          <span className="text-gray-700 text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={() => navigate(userType.route)}
                      className={`w-full ${colors.button} text-white rounded-2xl py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300`}
                    >
                      Continua come {userType.type === 'candidato' ? 'Candidato' : 'Agenzia'}
                    </Button>

                    {/* Additional Info */}
                    <p className="text-xs text-gray-500 text-center mt-4">
                      {userType.type === 'candidato' 
                        ? 'Registrazione completamente gratuita • Nessun costo' 
                        : 'Prova gratuita disponibile • Accesso demo incluso'
                      }
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Trust Section */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 mb-6">Già utilizzato da professionisti in tutta Italia</p>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm">Sicuro e certificato</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm">GDPR compliant</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-sm">Supporto 24/7</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SceltaTipoUtente;